import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Badge } from "@shared/ui/badge";
import { Layers } from "lucide-react";
import { FormService, FormField } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { toast } from "@shared/hooks/use-toast";
import { normalizeDate } from "@shared/utils/dateUtils";

interface AddQuantityConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  utilityName?: string;
  defaultQuantity?: number;
  onAddConnection: (newConn: {
    quantity: number;
    startDate?: string;
  }) => void;
}

export const AddTrashConnectionModal: React.FC<
  AddQuantityConnectionModalProps
> = ({
  isOpen,
  onClose,
  utilityName = "Quantity",
  defaultQuantity = 1,
  onAddConnection,
}) => {
  const fields: FormField[] = [
    {
      name: "quantity",
      label: "Quantity",
      type: "number",
      required: true,
      min: 1,
      placeholder: "1",
      defaultValue: defaultQuantity,
      className: "col-span-1",
    },
    {
      name: "startDate",
      label: "Start Date",
      type: "datepicker",
      placeholder: "dd-mm-yyyy",
      required: false,
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    defaultValues: {
      quantity: defaultQuantity,
      startDate: undefined,
    },
  });

  // Re-populate quantity whenever defaultQuantity changes (i.e. each time modal opens)
  useEffect(() => {
    if (isOpen) {
      form.setValue("quantity", defaultQuantity);
    }
  }, [isOpen, defaultQuantity]);

  const handleSubmit = (data: any) => {
    const qty = Number(data.quantity);
    if (isNaN(qty) || qty < 1) {
      toast({
        variant: "destructive",
        description: "Quantity must be at least 1.",
      });
      return;
    }

    // Pass ISO date string if provided
    const startDate = normalizeDate(data.startDate);

    onAddConnection({ quantity: qty, startDate });

    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[520px] flex flex-col p-0 overflow-hidden rounded-2xl bg-white shadow-xl border border-gray-100">
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Layers className="w-5 h-5 text-blue-600" />
            <DialogTitle className="text-base font-semibold text-gray-900">
              Add {utilityName} Connection
            </DialogTitle>
            <Badge
              variant="secondary"
              className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium rounded-full px-2.5 py-0.5"
            >
              Quantity Based
            </Badge>
          </div>
        </DialogHeader>

        <div className="px-6 py-5">
          <DynamicForm
            form={form}
            fields={fields}
            onSubmit={handleSubmit}
            onReset={onClose}
            gridColumns={2}
            config={{
              layout: "vertical",
              showSubmitButton: true,
              submitButtonText: "Add Connection",
              showResetButton: true,
              resetButtonText: "Cancel",
              classes: BUTTON_FIELD_CLASSES,
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
